import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQuery = (state) => {
  const { filters, pagination } = state.auditTrail;
  const params = new URLSearchParams();
  params.append("page", pagination.page + 1);
  params.append("pageSize", pagination.pageSize);
  if (filters.search.trim()) {
    params.append("search", filters.search.trim());
  }
  if (filters.startDate) {
    params.append("startDate", formatJakartaStartOfDay(filters.startDate));
  }
  if (filters.endDate) {
    params.append("endDate", formatJakartaEndOfDay(filters.endDate));
  }
  return params.toString();
};

export const fetchAuditLogs = createAsyncThunk(
  "auditTrail/fetchLogs",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildQuery(state);
      const response = await fetch(
        `${API_BASE_URL}/admin-audit-logs?${query}`,
        {
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load audit logs.");
    }
  }
);

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const initialState = {
  items: [],
  meta: initialMeta,
  filters: {
    search: "",
    startDate: null,
    endDate: null,
  },
  pagination: {
    page: 0,
    pageSize: 10,
  },
  loading: false,
  error: null,
};

const auditTrailSlice = createSlice({
  name: "auditTrail",
  initialState,
  reducers: {
    setAuditSearch: (state, action) => {
      state.filters.search = action.payload || "";
      state.pagination.page = 0;
    },
    setAuditDateRange: (state, action) => {
      state.filters.startDate = action.payload.startDate || null;
      state.filters.endDate = action.payload.endDate || null;
      state.pagination.page = 0;
    },
    setAuditPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setAuditPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const items =
          payload.items ||
          payload.data?.items ||
          (Array.isArray(payload) ? payload : []);
        const meta = payload.meta || payload.data?.meta || {};
        state.items = Array.isArray(items) ? items : [];
        state.meta = {
          ...initialMeta,
          ...meta,
        };
        if (typeof state.meta.page === "number") {
          state.pagination.page = Math.max(state.meta.page - 1, 0);
        }
        if (typeof state.meta.pageSize === "number") {
          state.pagination.pageSize = state.meta.pageSize;
        }
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load audit logs.";
      });
  },
});

export const {
  setAuditSearch,
  setAuditDateRange,
  setAuditPage,
  setAuditPageSize,
} = auditTrailSlice.actions;

export const selectAuditLogs = (state) => state.auditTrail.items;
export const selectAuditMeta = (state) => state.auditTrail.meta;
export const selectAuditFilters = (state) => state.auditTrail.filters;
export const selectAuditPagination = (state) => state.auditTrail.pagination;
export const selectAuditLoading = (state) => state.auditTrail.loading;
export const selectAuditError = (state) => state.auditTrail.error;

export default auditTrailSlice.reducer;
